// src/components/RichText.jsx
import React, { useEffect, useRef, useState } from "react";

export default function RichText({
  label = "Description",
  value = "",
  onChange = () => {},
  placeholder = "Write details about this event…",
  minHeight = 220,
}) {
  const isBrowser = typeof window !== "undefined";
  const [loaded, setLoaded] = useState(false);
  const editorRef = useRef({ CKEditor: null, ClassicEditor: null });

  useEffect(() => {
    let mounted = true;
    if (!isBrowser) return;

    (async () => {
      try {
        const [{ CKEditor }, ClassicEditor] = await Promise.all([
          import("@ckeditor/ckeditor5-react"),
          import("@ckeditor/ckeditor5-build-classic"),
        ]);
        if (!mounted) return;
        editorRef.current.CKEditor = CKEditor;
        editorRef.current.ClassicEditor = ClassicEditor.default || ClassicEditor;
        setLoaded(true);
      } catch (e) {
        console.error("CKEditor load failed:", e);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isBrowser]);

  // SSR / not loaded fallback → simple textarea
  if (!isBrowser || !loaded) {
    return (
      <div>
        <label className="block font-semibold text-slate-700">{label}</label>
        <textarea
          className="mt-1 border w-full rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-amber-300"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={8}
          placeholder={placeholder}
        />
      </div>
    );
  }

  const { CKEditor, ClassicEditor } = editorRef.current;

  return (
    <div>
      <label className="block font-semibold text-slate-700">{label}</label>
      <div className="mt-2 rounded-xl border overflow-hidden bg-white">
        <CKEditor
          editor={ClassicEditor}
          data={value || ""}
          config={{
            placeholder,
            toolbar: [
              "heading",
              "|",
              "bold",
              "italic",
              "underline",
              "link",
              "bulletedList",
              "numberedList",
              "blockQuote",
              "|",
              "alignment",
              "outdent",
              "indent",
              "|",
              "undo",
              "redo",
            ],
            heading: {
              options: [
                { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
                { model: "heading2", view: "h2", title: "Heading 2", class: "ck-heading_heading2" },
                { model: "heading3", view: "h3", title: "Heading 3", class: "ck-heading_heading3" },
              ],
            },
          }}
          onChange={(_, editor) => onChange(editor.getData())}
          onReady={(editor) => {
            const editable = editor.ui.view.editable.element;
            editable.style.minHeight = `${minHeight}px`;
          }}
        />
      </div>
      <style>{`
        .ck.ck-editor__editable:not(.ck-editor__nested-editable):focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(251, 191, 36, .35);
        }
      `}</style>
    </div>
  );
}
